'use client';

import ActionButton from '@/components/form/ActionButton/ActionButton';
import ImageDetailModal from '@/components/ui/ImageDetailModal/ImageDetailModal';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import { CART_ROUTE, DISCONTINUED_MESSAGE, PRODUCT_TYPE, SALE_STATUS, SHIPPING_DATE_TEXT } from '@/constants';
import { postCartReorder } from '@/features/account/api/postCartReorder';
import DesignShareButton from '@/features/account/components/DesignShareButton/DesignShareButton';
import type { PurchaseHistoryListItem, PurchaseHistoryProductItem } from '@/features/account/types';
import { Format } from '@/lib/dateFns';
import { swal } from '@/lib/sweetalert';
import { useGlobalLoadingAtom } from '@/state';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { formatCurrency, formatDateCommon } from '@/utils/formatters';
import { pickErrorMessages } from '@/utils/functions';
import { sendAddToCartEvent, sendClickPurchaseHistoryRebuyEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { buildProductOptionDisplay } from '@/utils/productOption';
import { PH_NO_CAPTURE_CLASS } from '@monorepo/common';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import styles from './GfPurchaseHistoryDetail.module.scss';

function GfPurchaseHistoryDetail({
  detail,
  isGuest = false,
}: {
  detail: PurchaseHistoryListItem;
  isGuest?: boolean;
}) {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const router = useRouter();
  const [, startGlobalLoading, , forceStopGlobalLoading] = useGlobalLoadingAtom();

  const handleImageClick = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImageUrl(null);
  };

  // もう一度購入するボタンのハンドラ
  const handleReDesignClick = async (item: PurchaseHistoryProductItem) => {
    if (item.userPrintId === undefined) return;

    try {
      startGlobalLoading();
      await postCartReorder({ printId: item.userPrintId });
      sendAddToCartEvent(
        [
          {
            item_id: item.productId.toString(),
            item_name: item.productName,
            item_brand: item.workName,
            item_variant: [item.productColor, item.productSize].filter(Boolean).join(' / '),
            price: item.basePrice,
            quantity: item.quantity,
            workId: item.workId,
            workName: item.workName,
            cartRoute: CART_ROUTE.reorder,
            entryRoute: CART_ROUTE.reorder,
            basedOnUserDesign: false,
            fromShareUrl: false,
          },
        ],
        { isGuest },
      );
      // API成功後にGAイベント送信
      sendClickPurchaseHistoryRebuyEvent(detail.orderId, item.userPrintId);
      router.push('/cart');
      router.refresh();
    } catch (error) {
      forceStopGlobalLoading();
      await swal.messages({ messages: pickErrorMessages(error) });
    }
  };

  const discountAmount = detail.discountAmount || 0;

  // 割引なしの時はoriginalTaxIncludedPriceがnullに設定されるので、値がnullの時は通常の税込価格を表示
  const dispTaxIncludedPrice = detail.originalTaxIncludedPrice ?? detail.taxIncludedPrice;

  return (
    <div className={styles.componentStyle}>
      <div className={styles.componentContainer}>
        <div className={styles.orderDateContainer}>
          <p className={styles.orderDate}>{formatDateCommon(detail.orderDate, Format.dateJa)}</p>
          <p className={styles.orderId}>ご注文ID：{detail.orderId}</p>
        </div>
        <div className={styles.deliveryStatusContainer}>
          <p className={styles.deliveryStatusText}>{detail.deliveryStatus}</p>
          <p className={styles.deliveryStatusText}>{SHIPPING_DATE_TEXT}</p>
        </div>
        <div className={styles.itemListContainer}>
          {detail.items.map((item) => {
            const isItemDiscontinued = item.productSaleStatus === SALE_STATUS.discontinued;
            const { attributes: optionAttributes, basePriceLabel } = buildProductOptionDisplay({
              productType: item.productType,
              colorName: item.selectedColorName,
              sizeName: item.selectedSizeName,
            });
            const isTemplateProduct = item.productType === PRODUCT_TYPE.template;
            return (
              <div key={item.id}>
                <div className={styles.itemContainer}>
                  <div className={styles.previewImageContainer}>
                    {item.prints.length > 0 &&
                      item.prints.map((print) => (
                        <div
                          key={print.id}
                          className={styles.printAreaImageWrapper}
                          onClick={() => handleImageClick(print.previewImageUrl)}
                        >
                          {print.shareable && (
                            <div
                              className={styles.shareButtonPosition}
                              onClick={(e) => e.stopPropagation()}
                              role='presentation'
                            >
                              <DesignShareButton shareToken={print.shareToken} disabled={isItemDiscontinued} />
                            </div>
                          )}
                          <NextImageView
                            containerClassName={styles.printAreaImageContainer}
                            className={styles.printAreaImageImg}
                            src={print.previewImageUrl}
                            alt='Preview'
                            fill={false}
                            width={120}
                            height={120}
                            sizes={getImageLayoutSizes({ sp: '90vw', pc: '221px' })}
                            objectFit='contain'
                          />
                        </div>
                      ))}
                  </div>
                  <div className={styles.itemInfoContainer}>
                    <div className={styles.itemSpecContainer}>
                      <div className={styles.productName}>
                        <p>{item.productName}</p>
                        <p className={styles.workName}>{item.workName}</p>
                      </div>
                      <div className={styles.selectedOptionContainer}>
                        {optionAttributes.map((attr) => (
                          <p key={attr.label}>
                            {attr.label}：{attr.value}
                          </p>
                        ))}
                        {item.productSpecs.map((spec) => (
                          <p key={spec.key}>
                            {spec.key}：{spec.value}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className={styles.itemSubTotalAmountContainer}>
                      {/* 基本価格表示 */}
                      <div className={styles.basePriceContainer}>
                        <div>
                          <p>基本料金</p>
                          <p className={styles.basePriceOption}>（{basePriceLabel}）</p>
                        </div>
                        <p>
                          <span className={styles.basePriceCurrency}>¥</span>
                          {formatCurrency(item.basePrice)}
                        </p>
                      </div>
                      {!isTemplateProduct &&
                        Object.entries(item.breakdownPrices).map(([key, value]) => (
                          <div key={key} className={styles.breakdownPriceItemContainer}>
                            <p>{key}</p>
                            <p>
                              <span className={styles.breakdownPriceCurrency}>¥</span>
                              {formatCurrency(value)}
                            </p>
                          </div>
                        ))}
                      <div className={styles.itemQuantityContainer}>
                        <p>数量</p>
                        <p>{item.quantity}</p>
                      </div>
                      <div className={styles.subTotalAmountContainer}>
                        <p className={styles.subTotalAmountText}>小計</p>
                        <p className={styles.subTotalAmount}>
                          <span className={styles.subTotalAmountCurrency}>¥</span>
                          {formatCurrency(item.subtotal)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {!isGuest && item.userPrintId !== undefined && (
                  <div className={styles.reDesignButtonWrapper}>
                    {isItemDiscontinued && <p className={styles.discontinuedMessage}>{DISCONTINUED_MESSAGE}</p>}
                    <ActionButton
                      onClick={() => handleReDesignClick(item)}
                      size='small'
                      color='black'
                      bgColor='white'
                      outlined
                      borderColor='gray300'
                      dataTestId='redesign-button'
                      disabled={isItemDiscontinued}
                    >
                      <div className={styles.reDesignButtonContent}>
                        <span className={styles.reDesignButtonText}>もう一度購入する</span>
                        <RoundArrowIcon direction='right' color='red' isBold bgColor='white' borderColor='red' />
                      </div>
                    </ActionButton>
                  </div>
                )}
                <hr className={styles.itemHr} />
              </div>
            );
          })}
        </div>
        <div className={styles.purchaseInfoContainer}>
          <div className={styles.deliveryAddressCardContainer}>
            {/* 配送先（氏名・住所）は PII のため Autocapture から除外する（GF-1936） */}
            <div className={`${styles.deliveryAddressCard} ${PH_NO_CAPTURE_CLASS}`}>
              <p className={styles.purchaseInfoTitle}>配送先</p>
              <p className={styles.purchaseInfoText}>{detail.userName}</p>
              <p className={styles.purchaseInfoText}>
                〒{detail.customerInfo.zip}&nbsp;
                <span className={utilsStyles.onlyPc}>
                  <br />
                </span>
                {detail.customerInfo.province}
                {detail.customerInfo.city}
                {detail.customerInfo.address1}&nbsp;{detail.customerInfo.address2}
              </p>
            </div>
            <div>
              <p className={styles.purchaseInfoTitle}>お支払い方法</p>
              <p className={styles.purchaseInfoText}>{detail.paymentMethodLabel}</p>
            </div>
          </div>

          <div className={styles.allItemSubTotalContainer}>
            <div className={styles.totalAllItemAmountItemContainer}>
              <p>グッズ小計</p>
              <p>
                <span className={styles.totalAllItemAmountCurrency}>¥</span>
                {formatCurrency(dispTaxIncludedPrice)}
              </p>
            </div>
            <div className={styles.totalAllItemAmountItemContainer}>
              <p>送料</p>
              <p>
                <span className={styles.totalAllItemAmountCurrency}>¥</span>
                {formatCurrency(detail.originalShippingFee)}
              </p>
            </div>
            {discountAmount > 0 && (
              <div className={styles.totalAllItemAmountItemContainer}>
                <p>割引</p>
                <p className={styles.discountAmount}>
                  -<span className={styles.discountAmountCurrency}>¥</span>
                  {formatCurrency(discountAmount)}
                </p>
              </div>
            )}
            <div className={styles.totalAmountContainer}>
              <p>合計</p>
              <p>
                <span className={styles.totalAmountCurrency}>¥</span>
                {formatCurrency(detail.totalAmount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 画像拡大表示モーダル */}
      <ImageDetailModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        imageUrl={selectedImageUrl}
        alt='拡大プレビュー'
      />
    </div>
  );
}

export default GfPurchaseHistoryDetail;
